import { Form, FormikProvider, useFormik } from 'formik';
import { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';
import { useDebounce } from 'use-debounce';

import { useSearch } from '@treams/treams-hooks';
import {
	AddIcon,
	Avatar,
	Button,
	CircleUserIcon,
	Empty,
	FlexGap,
	Heading3,
	Input,
	Label,
	SearchIcon,
	Select,
	Separator,
	tokens,
} from '@treams/treams-ui';

import validator from '../../../kakapo-ui-services/factories/ValidatorFactory';
import {
	Recipient,
	RecipientUserOption,
	isUserRecipientOption,
} from '../../../kakapo-ui-services/models/Recipient';
import RecipientService from '../../../kakapo-ui-services/services/recipients/RecipientsService';
import RecipientFieldReadonly from './RecipientFieldReadonly';

interface IProps {
	initialRecipients?: Array<Recipient>;
	excludeRecipients?: Array<Recipient>;
	onChange: (recipients: Array<Recipient>) => void;
	footer: ReactNode;
}

const transformOptionToUserRecipient = (option: RecipientUserOption): Recipient => {
	const { data, value } = option;
	return {
		userId: value,
		userData: data!,
	};
};

export const RecipientSelectorControl = function RecipientSelectorControl(props: {
	recipients: Recipient[];
	onAddRecipient: (recipient: Recipient) => void;
	autoFocus?: boolean;
	isDisabled?: boolean;
}) {
	const { t } = useTranslation();

	const [options, setOptions] = useState<Array<RecipientUserOption>>([]);
	const [inputValue, setInputValue] = useState('');

	const [inputValueDebounced] = useDebounce(inputValue, 200);

	const { results } = useSearch(inputValueDebounced, {
		skip: inputValueDebounced.length === 0,
		filters: 'isArchived:false',
	});

	useEffect(() => {
		if (results.length === 0) {
			setOptions([]);
			return;
		}

		setOptions(
			results.map((user) => ({
				label: `${user.firstName} ${user.lastName}`,
				value: user.id,
				data: {
					...user,
					userId: user.id,
				} as unknown as RecipientUserOption['data'],
			})),
		);
	}, [results]);

	const available = RecipientService.filterPreviouslyAddedOptions(options, props.recipients);
	const userOptions = available.filter(isUserRecipientOption).map((option) => ({
		value: option.value,
		title: option.label,
		icon: option.data ? <Avatar user={option.data} size={24} /> : undefined,
	}));

	return (
		<Select
			placeholder={t('recipient.selector.colleague.search')}
			searchPlaceholder={t('recipient.selector.colleague.search')}
			leading={<SearchIcon size={16} />}
			tint={false}
			search
			disableFilter
			autofocus={props.autoFocus}
			disabled={props.isDisabled}
			onQueryChange={setInputValue}
			options={
				userOptions.length > 0
					? userOptions
					: [
							{
								value: '__empty',
								title:
									inputValue.length > 0
										? t('recipients.selector.noResults')
										: t('recipients.selector.startTypingToSearch'),
								disabled: true,
							},
						]
			}
			onChange={(value) => {
				const option = available.find((item) => item.value === value);
				if (option && isUserRecipientOption(option)) {
					props.onAddRecipient(transformOptionToUserRecipient(option));
				}
			}}
		/>
	);
};

function ExternalRecipientSelector(props: {
	onAddRecipient: (recipient: { name: string; email: string }) => void;
}) {
	const { t } = useTranslation();

	const formik = useFormik({
		initialValues: {
			name: '',
			email: '',
		},
		validationSchema: validator.object({
			name: validator.string().required(),
			email: validator.string().email().required(),
		}),
		onSubmit: (values, { resetForm, setTouched }) => {
			props.onAddRecipient(values);
			(document.activeElement as HTMLElement | null)?.blur();
			resetForm();
			setTouched({ name: false, email: false });
		},
	});

	return (
		<FormikProvider value={formik}>
			<Form>
				<FlexGap flexDirection="column" gap={tokens.gap.list}>
					<FlexGap flexDirection="column" gap={tokens.gap.element}>
						<Label>{t('recipient.selector.external.fullName')}</Label>
						<Input.Formik name="name" />
					</FlexGap>
					<FlexGap flexDirection="column" gap={tokens.gap.element}>
						<Label>{t('recipient.selector.external.email')}</Label>
						<Input.Formik name="email" />
					</FlexGap>
					<Button
						small
						type="transparent"
						onClick={() => formik.submitForm()}
						title={t('recipient.selector.external.submit')}
					/>
				</FlexGap>
			</Form>
		</FormikProvider>
	);
}

const RecipientSelector = ({ initialRecipients, excludeRecipients, onChange, footer }: IProps) => {
	const { t } = useTranslation();

	const [recipients, setRecipients] = useState<Array<Recipient>>(initialRecipients ?? []);
	const [showExternal, setShowExternal] = useState(false);
	const excluded = excludeRecipients ? recipients.concat(excludeRecipients) : recipients;

	useEffect(() => {
		onChange(recipients);
	}, [recipients]);

	const removeRecipientAtIndex = (index: number): void => {
		const recipientsCopy = [...recipients];
		recipientsCopy.splice(index, 1);
		setRecipients(recipientsCopy);
	};

	return (
		<FlexGap gap={tokens.gap.section} alignItems="stretch">
			<FlexGap flex={1} minWidth={0} flexDirection="column" gap={tokens.gap.group}>
				<Heading3>{t('recipient.selector.add.title')}</Heading3>
				<RecipientSelectorControl
					key={excluded.length}
					recipients={excluded}
					onAddRecipient={(recipient) => setRecipients([...recipients, recipient])}
				/>
				{showExternal ? (
					<ExternalRecipientSelector
						onAddRecipient={(recipient) => {
							setRecipients([...recipients, recipient]);
							setShowExternal(false);
						}}
					/>
				) : (
					<Box>
						<Button
							type="transparent"
							icon={<AddIcon />}
							title={t('recipient.selector.external.title')}
							onClick={() => setShowExternal(true)}
						/>
					</Box>
				)}
				{footer != null && (
					<>
						<Separator />
						{footer}
					</>
				)}
			</FlexGap>
			<Separator vertical />
			<FlexGap
				width={256}
				flexShrink={0}
				flexDirection="column"
				gap={tokens.gap.list}
				alignItems="center"
			>
				<Heading3 alignSelf="stretch">{t('recipient.selector.list.title')}</Heading3>
				{recipients.length === 0 ? (
					<Empty icon={<CircleUserIcon />} description={t('recipient.selector.list.empty')} />
				) : (
					<FlexGap flexDirection="column" gap={tokens.gap.list} alignSelf="stretch">
						{recipients.map((recipient, index) => (
							<RecipientFieldReadonly
								key={index}
								recipient={recipient}
								onRemove={() => removeRecipientAtIndex(index)}
							/>
						))}
					</FlexGap>
				)}
			</FlexGap>
		</FlexGap>
	);
};

export default RecipientSelector;
